import React, { useEffect, useRef, useState } from 'react';
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder';
import { matchVoiceTopics, transcribeAudioAsTeacher } from '../../services/api';
import { VoiceTopicCandidate } from '../../types';

interface VoiceTopicCaptureProps {
  grade: number;
  section?: string | null;
  /** Confirmed a chapter candidate → expand + scroll to it in the checklist so
   *  the teacher can tick its sub-topics. */
  onFocusChapter: (chapterTitle: string) => void;
  /** No chapter matched → add the spoken text as the teacher's own topic. */
  onAddCustom: (text: string) => void;
}

type Phase = 'idle' | 'recording' | 'transcribing' | 'matching' | 'confirm' | 'nomatch' | 'error';

const SUBJECT_LABELS: Record<string, string> = {
  maths: 'Maths',
  science: 'Science',
  social: 'Social',
};

/**
 * "Tell us what you taught" — record a couple of sentences, transcribe them
 * with the teacher-authed STT endpoint, then match against the grade's NCERT
 * chapters and let the teacher confirm which chapters to add.
 */
const VoiceTopicCapture: React.FC<VoiceTopicCaptureProps> = ({
  grade,
  section,
  onFocusChapter,
  onAddCustom,
}) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [transcript, setTranscript] = useState('');
  const [candidates, setCandidates] = useState<VoiceTopicCandidate[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Live token for the match call; the interceptor keeps localStorage fresh.
  const gradeRef = useRef(grade);
  const sectionRef = useRef(section);
  useEffect(() => {
    gradeRef.current = grade;
    sectionRef.current = section;
  }, [grade, section]);

  const handleTranscript = async (text: string) => {
    setTranscript(text);
    setPhase('matching');
    try {
      const token = localStorage.getItem('teacherToken') || '';
      const res = await matchVoiceTopics(token, {
        transcript: text,
        grade: gradeRef.current,
        section: sectionRef.current,
      });
      const found = (res.candidates || []).slice(0, 3);
      if (found.length > 0) {
        setCandidates(found);
        setPhase('confirm');
      } else {
        setCandidates([]);
        setPhase('nomatch');
      }
    } catch (e: any) {
      setCandidates([]);
      setErrorMsg(e?.message || null);
      setPhase('nomatch');
    }
  };

  const {
    isSupported,
    isRecording,
    isTranscribing,
    error: recorderError,
    toggleRecording,
  } = useVoiceRecorder({
    onResult: handleTranscript,
    transcribe: (blob, name) => transcribeAudioAsTeacher(blob, name),
  });

  // Mirror the recorder's internal state into our phase machine.
  useEffect(() => {
    if (isRecording) {
      setPhase('recording');
    } else if (isTranscribing) {
      setPhase('transcribing');
    } else {
      setPhase((p) => {
        // Recording ended with no transcript (silence / cancel) → back to idle.
        if (p === 'recording' || p === 'transcribing') return 'idle';
        return p;
      });
    }
  }, [isRecording, isTranscribing]);

  useEffect(() => {
    if (recorderError) {
      setErrorMsg(recorderError);
      setPhase('error');
    }
  }, [recorderError]);

  if (!isSupported) return null;

  const reset = () => {
    setTranscript('');
    setCandidates([]);
    setErrorMsg(null);
    setPhase('idle');
  };

  // Confirm a chapter → jump the checklist to it so she can tick sub-topics.
  const focusCandidate = (c: VoiceTopicCandidate) => {
    onFocusChapter(c.title);
    reset();
  };

  const addTranscriptAsCustom = () => {
    const text = transcript.trim();
    if (text) {
      onAddCustom(text);
    }
    reset();
  };

  if (phase === 'idle') {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <button
          type="button"
          onClick={toggleRecording}
          className="w-full rounded-full bg-indigo-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-indigo-700"
        >
          🎤 Tell us what you taught
        </button>
        <p className="mt-2 text-center text-xs text-slate-500">
          Speak 2–3 sentences — we'll find the chapter
        </p>
      </div>
    );
  }

  if (phase === 'recording') {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <button
          type="button"
          onClick={toggleRecording}
          className="w-full animate-pulse rounded-full bg-red-500 px-4 py-3 text-sm font-medium text-white"
        >
          Listening… tap to stop
        </button>
      </div>
    );
  }

  if (phase === 'transcribing' || phase === 'matching') {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 text-center text-sm text-slate-500">
        {phase === 'transcribing' ? 'Transcribing your voice…' : 'Finding matching chapters…'}
      </div>
    );
  }

  if (phase === 'confirm') {
    return (
      <div className="space-y-3 rounded-xl border border-indigo-200 bg-indigo-50/50 p-4">
        <p className="text-sm text-slate-600">
          Heard: <span className="italic">"{transcript}"</span>
        </p>
        <div className="space-y-2">
          {candidates.map((c) => (
            <div
              key={`${c.subject}-${c.chapter_number}`}
              className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2"
            >
              <span className="text-sm text-slate-700">
                {SUBJECT_LABELS[c.subject] || c.subject} · Ch {c.chapter_number} — {c.title}
              </span>
              <button
                type="button"
                onClick={() => focusCandidate(c)}
                className="flex-shrink-0 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
              >
                Tick sub-topics →
              </button>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              reset();
              toggleRecording();
            }}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            Not this — try again
          </button>
          <button
            type="button"
            onClick={reset}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700"
          >
            I'll pick from the list
          </button>
        </div>
      </div>
    );
  }

  // nomatch / error: no candidates found, or the transcribe/match call failed.
  return (
    <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
      <p className="text-sm text-amber-800">
        {phase === 'error' && errorMsg ? errorMsg : "Couldn't find a matching chapter"}
      </p>
      {transcript.trim() && (
        <p className="text-sm text-slate-600">
          Heard: <span className="italic">"{transcript}"</span>
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {transcript.trim() && (
          <button
            type="button"
            onClick={addTranscriptAsCustom}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
          >
            Add "{transcript.trim()}" as your own topic
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            reset();
            toggleRecording();
          }}
          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
        >
          Try again
        </button>
      </div>
    </div>
  );
};

export default VoiceTopicCapture;
